import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'

import { KCommandGroup } from './cmdkTypes'

export type KCommandGroupLoader = () => KCommandGroup
export type KGroupProcessor = (arg: KCommandGroup) => KCommandGroup

type CommandStore = {
  groupLoaders: KCommandGroupLoader[]
  groupProcessors: KGroupProcessor[]
  query: string
  registerCommands: (cmds: KCommandGroupLoader) => void
  registerProcessor: (proc: KGroupProcessor) => void
}

const cmdStore = create<CommandStore>()(
  immer((set) => ({
    groupLoaders: [],
    groupProcessors: [],
    query: '',
    registerCommands: (cmds: KCommandGroupLoader) =>
      set((state) => {
        state.groupLoaders.push(cmds)
      }),
    registerProcessor: (proc: KGroupProcessor) =>
      set((state) => {
        state.groupProcessors.push(proc)
      }),
  }))
)

/**
 * Dedupe objects based on `obj[attr]`. Only last object with a given attr value is left.
 * Needed for local development hot reload, when we register the same group repeatedly.
 */
const dedupeBy = <T extends object, A extends keyof T>(attr: A, objs: T[]): T[] => {
  const vals = objs.map((obj) => obj[attr])
  return objs.filter((obj, index) => !vals.includes(obj[attr], index + 1))
}

const batchProcess = (procs: KGroupProcessor[]) => (groups: KCommandGroup[]) =>
  groups.map((grp) => procs.reduce((g, p) => p(g), grp))

export const createCommandGroups = () => {
  const { groupLoaders, groupProcessors } = cmdStore.getState()
  const groups = groupLoaders.map((L) => L())
  // We only know the name of the group after executing the loader,
  // so we need to dedupe on the way out, rather than the way in.
  const unique = dedupeBy('name', groups)
  const process = batchProcess(groupProcessors)
  return process(unique)
}

export const { registerCommands, registerProcessor } = cmdStore.getState()

export const getQuery = () => cmdStore.getState().query
export const setQuery = (query: string) => cmdStore.setState({ query })
